<template>

  <div
    class      = "timeline-container"
    :class     = "{scrubbing: isScrubbing}"
    v-hotkey   = "keymap"
    ref        = "timeline"
    @mousedown = "jumpTo"
    @mousewheel.prevent = "onScroll"
  >
    <!-- Playhead -->
    <div
      class      = "playhead"
      :class     = "{scrubbing: isScrubbing}"
      :style     = "{ left: playheadLeft + '%' }"
      @mousedown = "startScrubbing"
    >
      <div class = "playhead-circle"></div>
    </div>

    <!-- Ticks -->
    <div class = "ticks">
      <span v-for  = "(tick, index) in ticksToDisplay"
            :key   = "index"
            class  = "tick"
            :style = "{ left: tick.left + '%' }"
      >
        <div class = "time">{{ tick.time }}</div>
      </span>
      <span v-for  = "tick in smallTicks"
            :key   = "tick.left"
            class  = "small-tick"
            :style = "{ left: tick + '%' }"
      ></span>
    </div>

    <!-- Markers -->

    <div
      v-if    = "markerSpanLeft !== -1 && markerSpanRight !== -1"
      class   = "marker-span"
      :style  = "{ left: markerSpanLeft + '%', right: markerSpanRight + '%' }"
    >
    </div>
    <div
      v-if    = "markInLeft !== -1"
      class   = "marker"
      :style  = "{ left: markInLeft + '%' }"
    >
    </div>
    <div
      v-if    = "markOutLeft !== -1"
      class   = "marker"
      :style  = "{ left: markOutLeft + '%' }"
    >
    </div>
    
    <!-- Waveform -->
    <WaveformCanvas
      ref    = "waveform"
      class  = "waveform"
      :start = "offset"
      :end   = "offset + visibleDuration"
    >
    </WaveformCanvas>

    <!-- Clip Markers -->
    <ClipMarkers
      v-if      = "showClipMarkers"
      class     = "clips"
      :markers  = "clipMarkers"
    >
    </ClipMarkers>

    <!-- Annotation Markers -->
    <AnnotationMarkers
      v-if      = "showAnnotationMarkers"
      class     = "annotations"
      :markers  = "annotationMarkers"
    >
    </AnnotationMarkers>

  </div>
</template>

<script>

// #region Imports

/* vuex */
import {mapState} from 'vuex';

/* Telestream */
import { ToFrames } from '@user-interface/timecode-utilities';
import {
  calculatePlayheadOffset,
  calculateTimelineOffset,
  generateTicks,
  SecondsToShortTime,
} from '@/utils/timeline-utilities';
import WaveformCanvas from '@/components/panes/components/timeline/WaveformCanvas';
import AnnotationMarkers from './AnnotationMarkers.vue';
import ClipMarkers from './ClipMarkers.vue';
import { getTimecode, nptToTimecodeSeconds } from '@/store/utilities/timecodes.utils';
import { 
  JUMP_TO_TIME, 
  PAUSE_VIDEO, 
  PLAY_VIDEO, 
  ZOOM_ON_WAVEFORM
} from '@/utils/action-types';
import { WAVEFORM_ELEMENTS } from '../../../../utils/constants';
import { NPT_TIMECODE_SOURCE } from '@/utils/constants';

// #endregion Imports

// #region Constants

const DEFAULT_LENGTH_IN_SECONDS       = 10;
const TICK_LABEL_WIDTH                = 100;
const ZOOM_OUT_FACTOR                 = 1.1;
const ZOOM_IN_FACTOR                  = 0.9;
const MAX_ZOOM                        = 1;

const SCRUB_SCROLL_BACK_START         = 0.25;
const SCRUB_SCROLL_FORWARD_START      = 0.75;
const OFFSET_LOOP_DELAY_MS            = 50;

// #endregon Constants

export default {
  name:       'Timeline',
  components: {
    AnnotationMarkers,
    ClipMarkers,
    WaveformCanvas
  },
  data() {
    return {
      tickStepSize:       2,
      offsetLoopHandle:   undefined,
      isScrubbing:        false,
      scrubOffset:        undefined,
      scrubShift:         0,
      playAfterScrubbing: false,
      isZoomOutTooFar:    false,
      isZoomInTooFar:     false
    };
  },
  computed: {
    ...mapState(['clips', 'videos', 'videoPath', 'annotations', 'experimentalFeatures', 'ui']),
    timeDisplaySource() {
      return this.$store.state.timeDisplaySource;
    },
    fps() {
      return this.$store.state.fps;
    },
    videoElement() {
      return this.$store.state.video.element;
    },
    duration() {
      return this.$store.state.video.duration;
    },
    currentTime() {
      return this.$store.state.video.currentTime;
    },
    offset() {
      if (this.isScrubbing) {
        return this.scrubOffset;
      } else {
        return calculateTimelineOffset(this.currentTime, this.duration, this.zoom);
      }
    },
    visibleDuration() {
      return this.duration * this.zoom;
    },
    playheadLeft() {
      return calculatePlayheadOffset(this.currentTime, this.offset, this.duration, this.zoom);
    },
    markerSpanLeft() {
      const inMarkerTime = this.$store.state.video.inMarker;
      if (!inMarkerTime) {
        return -1;
      } else if (inMarkerTime < this.offset) {
        return 0;
      } else if (inMarkerTime > (this.offset + (this.duration * this.zoom))) {
        return -1;
      } else {
        return calculatePlayheadOffset(inMarkerTime, this.offset, this.duration, this.zoom);
      }
    },
    markerSpanRight() {
      const outMarkerTime = this.$store.state.video.outMarker;
      if (!outMarkerTime) {
        return -1;
      } else if (outMarkerTime < this.offset) {
        return -1;
      } else if (outMarkerTime > (this.offset + (this.duration * this.zoom))) {
        return 0;
      } else {
        return 100 - calculatePlayheadOffset(outMarkerTime, this.offset, this.duration, this.zoom);
      }
    },
    markInLeft() {
      const inMarkerTime = this.$store.state.video.inMarker;
      if (!inMarkerTime) {
        return -1;
      }

      if (
        (inMarkerTime < this.offset) ||
        (inMarkerTime > this.offset + (this.duration * this.zoom))
      ) {
        return -1;
      }

      return calculatePlayheadOffset(inMarkerTime, this.offset, this.duration, this.zoom);
    },
    markOutLeft() {
      const outMarkerTime = this.$store.state.video.outMarker;
      if (!outMarkerTime) {
        return -1;
      }

      if (
        (outMarkerTime < this.offset) ||
        (outMarkerTime > this.offset + (this.duration * this.zoom))
      ) {
        return -1;
      }

      return calculatePlayheadOffset(outMarkerTime, this.offset, this.duration, this.zoom);
    },
    ticks(){
      return generateTicks(this.offset, this.duration, this.zoom, this.tickStepSize);
    },
    ticksToDisplay() {
      return this.handleTicksTimeFormat(this.ticks, this.timeDisplaySource, this.videoElement, this.fps);
    },
    defaultZoom() {
      return DEFAULT_LENGTH_IN_SECONDS / this.duration;
    },
    smallTicks() {
      if (this.ticksToDisplay.length) {
        let smallTicks = [];
        let tickWidth = this.ticksToDisplay[1].left - this.ticksToDisplay[0].left;
        for (let i = 0; i < this.ticksToDisplay.length; i++) {
          for (let j = 1; j < 5; j++) {
            smallTicks.push(this.ticksToDisplay[i].left + (tickWidth / 5) * j);
          }
        }
        return smallTicks;
      }
      return [];
    },
    clipMarkers() {
      return this.clips.clips
        .filter(clip => clip.path === this.videoPath)
        .map(clip=> {
          const framerate = this.$store.state.video.framerate;
          const frameDuration = (1 / framerate);

          const startTime = (clip.in / framerate) + (frameDuration / 2);
          const endTime = (clip.out / framerate) + (frameDuration / 2);

          return {
            id:    clip.id,
            color: clip.color,
            time:  startTime,
            start: this.getMarkerLeft(startTime),
            end:   this.getMarkerLeft(endTime)
          }
        });
    },
    annotationMarkers() {
      return this.annotations.annotations.map(annotation => {
        const framerate = this.$store.state.video.framerate;
        const frameDuration = (1 / framerate);

        const startTime = (annotation.frame / framerate) + (frameDuration / 2);
        const endTime = ((annotation.frame + annotation.duration) / framerate) + (frameDuration / 2);

        return {
          id:    annotation.id,
          time:  startTime,
          start: this.getMarkerLeft(startTime),
          end:   this.getMarkerLeft(endTime),
          color: annotation.color,
          text:  annotation.text
        }
      });
    },
    selectedWaveformMarks() {
      return this.$store.state.video.selectedWaveformMarks;
    },
    showClipMarkers() {
      return this.selectedWaveformMarks.includes(WAVEFORM_ELEMENTS.CLIPS);
    },
    showAnnotationMarkers() {
      let experimental = this.experimentalFeatures && this.experimentalFeatures.annotations;
      let selected = this.selectedWaveformMarks.includes(WAVEFORM_ELEMENTS.ANNOTATIONS)
      return experimental && selected;
    },
    keymap() {
      return {
        '-':        () => this.zoomOut(),
        'numpad -': () => this.zoomOut(),
        '++':       () => this.zoomIn(),  // ++ since + is a keyword in v-hotkey
        '①':        () => this.zoomIn()   // Numpad +
      };
    },
    zoom: {
      get() {
        return this.ui.waveformZoomLevel;
      },
      set(value) {
        this.$store.dispatch(ZOOM_ON_WAVEFORM, value);
      }
    }
  },

  watch: {
    duration(currentDuration, previousDuration) {
      // On max zoom, make sure to update the ticks so they don't overlap:
      if (this.zoom === MAX_ZOOM) {    
        this.updateStepSize();
      }
      
      // Otherwise, try to keep the same range in view:
      else {
        const previousVisibleDuration = this.zoom * previousDuration;
        this.zoom = previousVisibleDuration / currentDuration;
      }
    },
    ticks(value){
      this.isZoomOutTooFar = value[value.length - 1].time > this.duration;
      this.isZoomInTooFar = value.length <= 2;
    }
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.zoom = this.defaultZoom;
    window.addEventListener('resize', this.updateStepSize);
    window.addEventListener('mousemove', this.scrub);
    window.addEventListener('mouseup', this.endScrubbing);

    this.runOffsetLoop();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateStepSize);
    window.removeEventListener('mousemove', this.scrub);
    window.removeEventListener('mouseup', this.endScrubbing);

    if (this.offsetLoopHandle) {
      clearTimeout(this.offsetLoopHandle);
    }
  },

  // #endregion Life-Cycle

  // #region Operations

  methods: {
    // Make sure tick labels fit the size of the screen:
    updateStepSize() {
      const visibleDuration = this.duration * this.zoom;
      const targetForTicks = this.$refs.timeline.clientWidth / TICK_LABEL_WIDTH - 1;
      const ratio = Math.round(visibleDuration / targetForTicks);

      this.tickStepSize = ratio > 0 ? ratio : 1;
    },

    zoomOut() {
      if (this.isZoomOutTooFar) return;
      this.zoom = Math.min(1, this.zoom * ZOOM_OUT_FACTOR);
      this.updateStepSize();

      this.$refs.waveform.render();
    },

    zoomIn() {
      if (this.isZoomInTooFar) return
      this.zoom = this.zoom * ZOOM_IN_FACTOR;
      this.updateStepSize();

      this.$refs.waveform.render();
    },

    zoomAllTheWayOut() {
      this.zoom = MAX_ZOOM;
      this.updateStepSize();

      this.$refs.waveform.render();
    },

    zoomAtDefault() {
      this.zoom = this.defaultZoom;
      this.updateStepSize();

      this.$refs.waveform.render();
    },

    onScroll(e) {
      if (!e.ctrlKey) {
        return;
      }

      if (e.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    },

    handleTicksTimeFormat: function (ticks, timeDisplaySource, videoElement) {
      if (timeDisplaySource && timeDisplaySource.type === 'timecode' && videoElement) {
        return ticks.map((tick) => {
          if (tick.time !== -1) {
            let timecode = getTimecode(videoElement, tick.time, timeDisplaySource.name);
            if (timecode) {
              if (timeDisplaySource.name === NPT_TIMECODE_SOURCE.name) {
                timecode = nptToTimecodeSeconds(timecode).split('@')[0];
              }
              else {
                timecode = timecode.split('@')[0]
              }
            }
            return {time: timecode, left: tick.left};
          } else {
            return {time: 0, left: tick.left}
          }
        });
      } else if (timeDisplaySource.type === 'frames') {
        return ticks.map(t => ({
          time: ToFrames(getTimecode(videoElement, t.time, timeDisplaySource.name)),
          left: t.left
        }));
      } else {
        return ticks.map((tick) => {
          return {time: SecondsToShortTime(tick.time), left: tick.left};
        });
      }
    },

    jumpTo(event) {
      let timelineRect = this.$refs.timeline.getBoundingClientRect();
      let clickPositionXOnTimeline = event.clientX - timelineRect.x;
      let percentage = clickPositionXOnTimeline / timelineRect.width;
      let timelineDuration = this.offset + this.visibleDuration - this.offset;
      this.$store.dispatch(JUMP_TO_TIME, percentage * timelineDuration + this.offset);
    },

    // #region Scrubbing

    startScrubbing(event) {
      event.stopPropagation();
      this.scrubOffset = this.offset;
      this.isScrubbing = true;

      this.$store.dispatch(PAUSE_VIDEO);
      this.playAfterScrubbing = !this.videoElement.paused;
    },

    endScrubbing() {
      if (!this.isScrubbing) {
        return;
      }

      this.isScrubbing = false;
      this.scrubShift = 0;

      if (this.playAfterScrubbing) {
        this.$store.dispatch(PLAY_VIDEO);
      }
    },

    scrub(event) {
      if (!this.isScrubbing) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const timelineRect = this.$refs.timeline.getBoundingClientRect();
      const cursorPosition = event.clientX - timelineRect.x;
      const percentage = this.clamp(cursorPosition / timelineRect.width, 0.0, 1.0);
      const visibleDuration = this.duration * this.zoom;

      this.$store.dispatch(JUMP_TO_TIME, this.scrubOffset + (percentage * visibleDuration));

      if (percentage < SCRUB_SCROLL_BACK_START) {
        this.scrubShift = (percentage - SCRUB_SCROLL_BACK_START) * visibleDuration;
      } else if (percentage > SCRUB_SCROLL_FORWARD_START) {
        this.scrubShift = (percentage - SCRUB_SCROLL_FORWARD_START) * visibleDuration;
      } else {
        this.scrubShift = 0;
      }
    },

    adjustWindowOffset() {
      if (!this.isScrubbing || (this.scrubShift === 0)) {
        return;
      }

      const newOffset = this.clamp(
        this.scrubOffset + this.scrubShift,
        0,
        this.duration - (this.duration * this.zoom)
      );

      if (newOffset !== this.scrubOffset) {
        this.scrubOffset = newOffset;
        this.$store.dispatch(JUMP_TO_TIME, this.currentTime + this.scrubShift);
      }
    },

    runOffsetLoop() {
      return setTimeout(
        () => {
          this.adjustWindowOffset();
          this.offsetLoopHandle = this.runOffsetLoop();
        },
        OFFSET_LOOP_DELAY_MS
      );
    },

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    },

    getMarkerLeft(time){
      return calculatePlayheadOffset(time, this.offset, this.duration, this.zoom);
    }
    
    // #endregion Scrubbing
  },
};
</script>

<style scoped lang = "scss">

$ticks-height             : 40px;
$tick-height              : 5px;
$timeline-tick-color      : #434b5c;
$timeline-item-background : #3f64aa;
$timeline-item-color      : #b6bdcb;
$playhead-color           : #0099ee;
$marker-color             : yellow;

.timeline-container {
  position        : relative;
  display         : flex;
  flex-direction  : column;
  overflow        : hidden;

  cursor          : pointer;
  &.scrubbing {
    cursor          : grabbing;
  }

  .playhead {
    position         : absolute;
    height           : 100%;
    width            : 1px;
    background-color : $playhead-color;
    border-right     : 1px solid $playhead-color;
    border-left      : 1px solid $playhead-color;
    z-index          : 1;

    cursor           : grab;
    &.scrubbing {
      cursor          : grabbing;
    }

    .playhead-circle {
      height           : 14px;
      width            : 14px;
      background-color : $playhead-color;
      transform        : translateX(-50%);
      border-radius    : 50%;
    }
  }

  .ticks {
    height   : $ticks-height;
    position : relative;
    overflow : hidden;

    .tick {
      position         : absolute;
      display          : inline-block;

      width            : 1px;
      height           : 8px;
      background-color : white;

      .time {
        font-size   : 11px;
        color       : white;
        user-select : none;

        width       : 60px;
        text-align  : center;
        margin-left : -30px;
        margin-top  : 10px;
      }
    }

    .small-tick {
      position         : absolute;
      display          : inline-block;
      pointer-events   : none;

      width            : 1px;
      height           : $tick-height;
      background-color : white;
    }

    .waveform {
      flex : 1;
    }
  }

  .marker-span {
    position    : absolute;
    height      : $tick-height;
    background  : rgba($marker-color, 0.3);
  }

  .marker {
    position    : absolute;
    height      : $tick-height;
    width       : 2px;
    background  : $marker-color;
  }
  
}
</style>
